import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { catalogReadiness, parseProductMetadata } from "./product-metadata";
import type { StripeProduct } from "./types";

interface ProductMetadataSummaryProps {
  readonly product: StripeProduct;
}

const CHIP_CLASS =
  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-sans text-xs font-medium";

/**
 * Read-only badges for a Product's pricing-v2 metadata, shown in the
 * products list. Renders nothing for a non-subscription Product (no
 * tier / access / rank set); flags an `incomplete` Product so the
 * operator knows the public catalog would exclude it.
 */
export function ProductMetadataSummary({ product }: ProductMetadataSummaryProps) {
  const { t } = useTranslation();
  const meta = parseProductMetadata(product.metadata);
  const readiness = catalogReadiness(meta);

  if (readiness === "non-subscription") return null;

  return (
    <div
      data-testid={`product-metadata-${product.id}`}
      className="flex flex-wrap items-center gap-1.5"
    >
      {meta.tier && <span className={`${CHIP_CLASS} bg-primary/10 text-primary`}>{meta.tier}</span>}
      {meta.access.map((access) => (
        <span key={access} className={`${CHIP_CLASS} bg-muted text-muted-foreground`}>
          {t(`catalog.products.metadata.access.${access}`)}
        </span>
      ))}
      {meta.rank !== null && (
        <span className={`${CHIP_CLASS} bg-muted text-muted-foreground`}>
          {t("catalog.products.metadata.rankBadge", { rank: meta.rank })}
        </span>
      )}
      {readiness === "incomplete" && (
        <span
          data-testid={`product-metadata-incomplete-${product.id}`}
          role="alert"
          className={`${CHIP_CLASS} bg-amber-100 text-amber-800`}
        >
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          {t("catalog.products.metadata.incompleteBadge")}
        </span>
      )}
    </div>
  );
}
